import type { ProblemDetail } from '../types/api'

/**
 * The single fetch wrapper every request in the app goes through.
 *
 * Three things it guarantees, and the reason each exists:
 *
 *  * Every error becomes an {@link ApiError} carrying the parsed RFC 7807 body. The backend
 *    already returns one shape for every failure, so the UI never has to guess whether it
 *    has a string, an object, or an HTML error page.
 *  * The language goes on every request as `?lang=`. Content is translated server-side, so
 *    forgetting it on one call would show a single English card in an otherwise Hebrew page.
 *  * The admin token is attached from memory, never read from localStorage on each call.
 */

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? ''
const TOKEN_STORAGE_KEY = 'lakeside.auth'
/**
 * "Someone from the team has signed in to the console in this browser."
 *
 * Kept apart from the token on purpose. The token expires after two hours and is cleared on
 * any 401; this marker is only set by signing in and only removed by signing out. It gates
 * nothing secure - it decides whether the public site shows the team's note button, and the
 * server still checks the team code on every note.
 */
const TEAM_BROWSER_KEY = 'lakeside.team'

export class ApiError extends Error {
  readonly status: number
  readonly problem: ProblemDetail | null

  constructor(status: number, message: string, problem: ProblemDetail | null) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.problem = problem
  }

  /** Field-level validation messages, keyed by field name. Empty unless status is 400. */
  get fieldErrors(): Record<string, string> {
    return this.problem?.errors ?? {}
  }

  get isConflict(): boolean {
    return this.status === 409
  }

  get isUnauthorized(): boolean {
    return this.status === 401 || this.status === 403
  }
}

// ---------------------------------------------------------------- auth token

interface StoredAuth {
  accessToken: string
  refreshToken: string
  expiresAt: number
}

let authState: StoredAuth | null = readStoredAuth()

function readStoredAuth(): StoredAuth | null {
  try {
    const raw = window.localStorage.getItem(TOKEN_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as StoredAuth
    // A token that has already expired is worse than no token: it produces a 401 on the
    // first admin request instead of a clean redirect to the sign-in screen.
    return parsed.expiresAt > Date.now() ? parsed : null
  } catch {
    return null
  }
}

export function setAuth(accessToken: string, refreshToken: string, expiresInSeconds: number): void {
  // Renew a minute early so a request never starts with a token that expires mid-flight.
  authState = {
    accessToken,
    refreshToken,
    expiresAt: Date.now() + (expiresInSeconds - 60) * 1000,
  }
  try {
    window.localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(authState))
    window.localStorage.setItem(TEAM_BROWSER_KEY, '1')
  } catch {
    // Session-only auth is an acceptable degradation.
  }
}

/** Whether a team member has signed in to the console from this browser and not signed out. */
export function isTeamBrowser(): boolean {
  try {
    return window.localStorage.getItem(TEAM_BROWSER_KEY) === '1'
  } catch {
    return false
  }
}

/**
 * Marks this browser as the team's without a console sign-in - for Lena's and Naum's phones,
 * which open the site from a WhatsApp link and never visit the console. Same marker, same
 * consequence: the note button shows; the team code is still checked on every note.
 */
export function rememberTeamBrowser(): void {
  try {
    window.localStorage.setItem(TEAM_BROWSER_KEY, '1')
  } catch {
    /* a private window forgets it on close, which is acceptable */
  }
}

/** Explicit sign-out: the only thing that forgets this browser belongs to the team. */
export function forgetTeamBrowser(): void {
  try {
    window.localStorage.removeItem(TEAM_BROWSER_KEY)
  } catch {
    /* ignore */
  }
}

export function clearAuth(): void {
  authState = null
  try {
    window.localStorage.removeItem(TOKEN_STORAGE_KEY)
  } catch {
    /* ignore */
  }
}

export function getAccessToken(): string | null {
  if (authState && authState.expiresAt <= Date.now()) {
    clearAuth()
  }
  return authState?.accessToken ?? null
}

export function getRefreshToken(): string | null {
  return authState?.refreshToken ?? null
}

export function isAuthenticated(): boolean {
  return getAccessToken() !== null
}

// ---------------------------------------------------------------- request

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  body?: unknown
  /** Language for server-side content translation. Omit for endpoints with no content. */
  locale?: string
  query?: Record<string, string | number | boolean | undefined | null>
  /** Attach the admin bearer token. */
  authenticated?: boolean
  signal?: AbortSignal
}

function buildUrl(path: string, options: RequestOptions): string {
  const url = new URL(BASE_URL + path, window.location.origin)
  if (options.locale) {
    url.searchParams.set('lang', options.locale)
  }
  for (const [key, value] of Object.entries(options.query ?? {})) {
    if (value !== undefined && value !== null && value !== '') {
      url.searchParams.set(key, String(value))
    }
  }
  // The whole URL, not just the path.
  //
  // This returned `pathname + search` and threw the origin away, which made VITE_API_BASE_URL
  // decorative: every call went to the site's own origin. Invisible in development, where the
  // Vite dev server proxies /api to localhost:8087 and a relative path is exactly right, and
  // fatal in production, where nginx answers any unknown path with index.html and a 200 - so
  // the app parsed HTML as JSON and rendered empty pages with no error anywhere.
  //
  // With no base configured this still yields a same-origin URL, so the dev proxy keeps working.
  return url.toString()
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' }

  // FormData must NOT get an explicit Content-Type: the browser has to set it itself so that
  // it can append the multipart boundary. Setting it by hand produces a request the server
  // cannot parse, and the error it returns says nothing about why.
  const isMultipart = options.body instanceof FormData
  if (options.body !== undefined && !isMultipart) {
    headers['Content-Type'] = 'application/json'
  }
  if (options.authenticated) {
    const token = getAccessToken()
    if (token) {
      headers.Authorization = `Bearer ${token}`
    }
  }

  let response: Response
  try {
    response = await fetch(buildUrl(path, options), {
      method: options.method ?? 'GET',
      headers,
      body:
        options.body === undefined
          ? undefined
          : isMultipart
            ? (options.body as FormData)
            : JSON.stringify(options.body),
      signal: options.signal,
    })
  } catch (cause) {
    // fetch only rejects on a network-level failure, which for this app almost always
    // means the phone lost signal somewhere between Kerimäki and Savonlinna.
    throw new ApiError(0, 'Network unreachable', null)
  }

  if (response.status === 204) {
    return undefined as T
  }

  const text = await response.text()
  const parsed = text ? safeParse(text) : null

  if (!response.ok) {
    const problem = (parsed ?? null) as ProblemDetail | null
    if (response.status === 401) {
      clearAuth()
    }
    throw new ApiError(response.status, problem?.detail ?? response.statusText, problem)
  }

  return parsed as T
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text)
  } catch {
    return null
  }
}
